import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { ModalConfirm } from '../../../../../../shared/components';
import { Content, Reason } from '../../../../../../shared/components/feedback-form-content';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { useConferenceId } from '../../../../../../shared/hooks/useConferenceId';
import { CONFERENCE_ID_KEY, MESSAGE_KEY, REASONS_KEY } from '../../constants';
import { reportAProblemFormWasShownAction, sendReportAProblemAction } from '../../slice';
import styles from './styles.css';

export function ReportAProblemForm({ onClose }: { onClose: () => void }) {
    const [message, setMessage] = useState('');
    const conferenceId = useConferenceId();

    const { checkedReasons, addReason, deleteReason, onMessageChange } = useMemo(() => {
        const storedConferenceId = localStorage.getItem(CONFERENCE_ID_KEY);

        if (storedConferenceId !== conferenceId) {
            localStorage.removeItem(REASONS_KEY);
            localStorage.removeItem(MESSAGE_KEY);
        }

        const storedMessage = localStorage.getItem(MESSAGE_KEY);

        if (storedMessage) {
            setMessage(storedMessage);
        }

        const storedReasons = localStorage.getItem(REASONS_KEY);

        const checkedReasons = new Set<Reason>(storedReasons ? JSON.parse(storedReasons) : []);

        const addReason = (reason: Reason) => {
            checkedReasons.add(reason);

            localStorage.setItem(REASONS_KEY, JSON.stringify([...checkedReasons]));
        };

        const deleteReason = (reason: Reason) => {
            checkedReasons.delete(reason);

            localStorage.setItem(REASONS_KEY, JSON.stringify([...checkedReasons]));
        };

        const onMessageChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(value);

            localStorage.setItem(MESSAGE_KEY, value);
        };

        if (conferenceId) {
            localStorage.setItem(CONFERENCE_ID_KEY, conferenceId);
        }

        return { checkedReasons, addReason, deleteReason, onMessageChange };
    }, [conferenceId]);

    const { sendReport, formWasShown } = useActions({
        sendReport: sendReportAProblemAction,
        formWasShown: reportAProblemFormWasShownAction,
    });

    const onConfirm = useCallback(() => {
        sendReport({ reasons: [...checkedReasons], comment: message });

        onClose();
    }, [checkedReasons, message, onClose, sendReport]);

    const onCancel = useCallback(() => {
        countMetrika('meeting_screen', 'problem_button', 'cancel_from');

        onClose();
    }, [onClose]);

    useEffect(() => {
        formWasShown();
    }, [formWasShown]);

    return (
        <ModalConfirm
            onClose={onCancel}
            onConfirm={onConfirm}
            classNames={styles}
            headerText={i18n('feedback', 'report-a-problem-form-header')}
            confirmButtonText={i18n('feedback', 'submit')}
        >
            <Content
                message={message}
                checkedReasons={checkedReasons}
                addReason={addReason}
                deleteReason={deleteReason}
                onMessageChange={onMessageChange}
            />
        </ModalConfirm>
    );
}
